import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Chip,
  useTheme,
  alpha,
  InputAdornment,
  Skeleton,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InventoryIcon from "@mui/icons-material/Inventory";
import useParsedTSVCache from "../hooks/useParsedTSVCache";

// Status calculation based on months_supply_6mo
const getStatus = (row) => {
  const {
    stock_quantity,
    rolling_6mo_avg_monthly_qty_sold,
    months_supply_6mo,
  } = row;

  // Critical: Out of stock but has demand
  if (stock_quantity === 0 && rolling_6mo_avg_monthly_qty_sold > 0) {
    return {
      level: "CRITICAL",
      color: "#d32f2f",
      label: "Critical",
      priority: 1,
    };
  }

  // No demand data
  if (months_supply_6mo === -1.0) {
    return {
      level: "NO_DEMAND",
      color: "#9e9e9e",
      label: "No Demand",
      priority: 6,
    };
  }

  // Low stock: < 1 month supply
  if (months_supply_6mo < 1.0) {
    return { level: "LOW", color: "#f57c00", label: "Low Stock", priority: 2 };
  }

  // Moderate: 1-3 months supply
  if (months_supply_6mo >= 1.0 && months_supply_6mo < 3.0) {
    return {
      level: "MODERATE",
      color: "#fbc02d",
      label: "Moderate",
      priority: 3,
    };
  }

  // Healthy: 3-12 months supply
  if (months_supply_6mo >= 3.0 && months_supply_6mo <= 12.0) {
    return {
      level: "HEALTHY",
      color: "#388e3c",
      label: "Healthy",
      priority: 4,
    };
  }

  // Overstocked: > 12 months supply
  if (months_supply_6mo > 12.0) {
    return {
      level: "OVERSTOCKED",
      color: "#1976d2",
      label: "Overstocked",
      priority: 5,
    };
  }

  return { level: "UNKNOWN", color: "#9e9e9e", label: "Unknown", priority: 7 };
};

// Format numbers for display
const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return "N/A";
  if (value === -1.0) return "N/A";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "$0";
  return "$" + formatNumber(value, 0);
};

// Format large currency values compactly (e.g., 9.5k, 260k, 1.2M)
const formatCompactCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "$0";

  // Ensure value is a number
  const numValue = Number(value);
  if (isNaN(numValue)) return "$0";
  if (numValue === 0) return "$0";

  if (numValue >= 1000000) {
    const millions = numValue / 1000000;
    return (
      "$" + (millions < 10 ? millions.toFixed(1) : millions.toFixed(0)) + "M"
    );
  }
  if (numValue >= 1000) {
    const thousands = numValue / 1000;
    return (
      "$" + (thousands < 10 ? thousands.toFixed(1) : thousands.toFixed(0)) + "k"
    );
  }
  return "$" + numValue.toFixed(0);
};

// Calculate price comparison percentage
const calculatePriceVsAvg = (currentValue, avgValue) => {
  if (!avgValue || avgValue === 0) return null;
  if (!currentValue || currentValue === 0) return null;
  return (currentValue / avgValue) * 100;
};

const DailyStockReport = ({
  title = "Daily Stock Report",
  subtitle = "",
  initialVolumeFilter = null,
  initialStatusFilter = null,
  initialSearch = null,
  initialOrderBy = null,
  initialOrder = null,
  onFilterChange = () => {},
}) => {
  const theme = useTheme();
  const [error, setError] = useState(null);

  // Use parsed TSV cache with post-transform to calculate status during parsing
  // This ensures status is calculated once and cached with the parsed data (Option 3)
  const transformRow = (row) => ({
    ...row,
    status: getStatus(row),
    price_vs_avg: calculatePriceVsAvg(
      row.current_market_value,
      row.estimated_value_all_time,
    ),
  });

  const {
    data,
    loading,
    error: fetchError,
    lastModified,
  } = useParsedTSVCache(
    "markets_cache_daily_stock_report_v2", // Updated cache key to invalidate old data
    "/markets-data/latest-stock-report.tsv",
    {
      header: true,
      delimiter: "\t",
      skipEmptyLines: true,
      dynamicTyping: true,
    },
    3600000, // 1 hour TTL
    transformRow, // Post-parse transformation
  );

  const [orderBy, setOrderBy] = useState(initialOrderBy || "status");
  const [order, setOrder] = useState(initialOrder || "asc");
  const [searchQuery, setSearchQuery] = useState(initialSearch || "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    initialSearch || "",
  );
  const [volumeFilter, setVolumeFilter] = useState(
    initialVolumeFilter || "100",
  );
  const [statusFilter, setStatusFilter] = useState(
    initialStatusFilter || "ALL",
  );

  // Track initialization
  const [isInitialized, setIsInitialized] = useState(false);

  const volumeOptions = [
    { label: "All Volumes", value: "0" },
    { label: "Min $50/month", value: "50" },
    { label: "Min $100/month", value: "100" },
    { label: "Min $500/month", value: "500" },
    { label: "Min $1,000/month", value: "1000" },
    { label: "Min $5,000/month", value: "5000" },
  ];

  const statusOptions = [
    { label: "All Status", value: "ALL" },
    { label: "Critical", value: "CRITICAL" },
    { label: "Low Stock", value: "LOW" },
    { label: "Moderate", value: "MODERATE" },
    { label: "Healthy", value: "HEALTHY" },
    { label: "Overstocked", value: "OVERSTOCKED" },
    { label: "No Demand", value: "NO_DEMAND" },
  ];

  // Set error from fetch if any
  useEffect(() => {
    if (fetchError) {
      setError(`Error fetching data: ${fetchError}`);
    }
  }, [fetchError]);

  // Mark initialization complete after a short delay to prevent immediate URL updates
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Debounce search query to reduce filter calculations during rapid typing
  // Only updates debounced state, not URL (URL updates happen in handleSearchChange via onFilterChange)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);

    // Update URL with sorting params
    if (isInitialized) {
      onFilterChange({
        volume: volumeFilter,
        status: statusFilter,
        search: searchQuery,
        orderby: property,
        order: newOrder,
      });
    }
  };

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  const handleVolumeFilterChange = (value) => {
    setVolumeFilter(value);
    if (isInitialized) {
      onFilterChange({
        volume: value,
        status: statusFilter,
        search: searchQuery,
        orderby: orderBy,
        order: order,
      });
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    if (isInitialized) {
      onFilterChange({
        volume: volumeFilter,
        status: value,
        search: searchQuery,
        orderby: orderBy,
        order: order,
      });
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    // Debounce the URL update
    if (isInitialized) {
      const timer = setTimeout(() => {
        onFilterChange({
          volume: volumeFilter,
          status: statusFilter,
          search: value,
          orderby: orderBy,
          order: order,
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  };

  // Filter and sort data
  const filteredData = useMemo(() => {
    if (!data || loading) return [];

    return data.filter((row) => {
      // Volume filter
      const minVolume = parseFloat(volumeFilter);
      if (minVolume > 0 && row.rolling_6mo_avg_monthly_volume < minVolume) {
        return false;
      }

      // Status filter - safety check for status field
      if (statusFilter !== "ALL" && row.status?.level !== statusFilter) {
        return false;
      }

      // Search filter
      if (debouncedSearchQuery) {
        const query = debouncedSearchQuery.toLowerCase();
        return row.item_type?.toLowerCase().includes(query);
      }

      return true;
    });
  }, [data, volumeFilter, statusFilter, debouncedSearchQuery]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      let aVal, bVal;

      if (orderBy === "status") {
        // Safety check: ensure status exists (in case of old cached data)
        aVal = a.status?.priority ?? 999;
        bVal = b.status?.priority ?? 999;
      } else {
        aVal = a[orderBy];
        bVal = b[orderBy];
      }

      // Handle numeric values
      if (!isNaN(parseFloat(aVal)) && !isNaN(parseFloat(bVal))) {
        return order === "desc"
          ? parseFloat(bVal) - parseFloat(aVal)
          : parseFloat(aVal) - parseFloat(bVal);
      }

      // Handle string values
      const comparison = (aVal?.toString() || "").localeCompare(
        bVal?.toString() || "",
      );
      return order === "desc" ? -comparison : comparison;
    });
  }, [filteredData, orderBy, order]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!data || loading) {
      return {
        critical: 0,
        low: 0,
        moderate: 0,
        healthy: 0,
        overstocked: 0,
        noDemand: 0,
        total: 0,
        totalAtaValue: 0,
        totalListValue: 0,
        thirtyDayOpportunity: 0,
      };
    }

    const critical = data.filter(
      (row) => row.status?.level === "CRITICAL",
    ).length;
    const low = data.filter((row) => row.status?.level === "LOW").length;
    const moderate = data.filter(
      (row) => row.status?.level === "MODERATE",
    ).length;
    const healthy = data.filter(
      (row) => row.status?.level === "HEALTHY",
    ).length;
    const overstocked = data.filter(
      (row) => row.status?.level === "OVERSTOCKED",
    ).length;
    const noDemand = data.filter(
      (row) => row.status?.level === "NO_DEMAND",
    ).length;

    // Calculate financial metrics
    const totalAtaValue = data.reduce((sum, row) => {
      const value = Number(row.estimated_value_all_time) || 0;
      return sum + value;
    }, 0);

    const totalListValue = data.reduce((sum, row) => {
      const value = Number(row.current_market_value) || 0;
      return sum + value;
    }, 0);

    // Calculate 30-day opportunity: sum of (Monthly Volume * Monthly Supply) for items with supply < 1.0
    const thirtyDayOpportunity = data.reduce((sum, row) => {
      const supply = Number(row.months_supply_6mo);
      if (!isNaN(supply) && supply >= 0 && supply < 1.0) {
        const monthlyVolume = Number(row.rolling_6mo_avg_monthly_volume) || 0;
        const monthlySupply = supply || 0;
        return sum + monthlyVolume * monthlySupply;
      }
      return sum;
    }, 0);

    return {
      critical,
      low,
      moderate,
      healthy,
      overstocked,
      noDemand,
      total: data.length,
      totalAtaValue: Number(totalAtaValue) || 0,
      totalListValue: Number(totalListValue) || 0,
      thirtyDayOpportunity: Number(thirtyDayOpportunity) || 0,
    };
  }, [data]);

  if (error) {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <InventoryIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="h5">{title}</Typography>
        </Box>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <InventoryIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="h5">{title}</Typography>
        </Box>
        {lastModified && (
          <Typography variant="caption" color="text.secondary">
            Data from: {lastModified.toLocaleString()}
          </Typography>
        )}
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}

      {/* Summary Cards */}
      {!loading && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ bgcolor: alpha("#d32f2f", 0.1) }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography
                    variant="h4"
                    sx={{ color: "#d32f2f", fontWeight: "bold" }}
                  >
                    {summaryStats.critical}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Critical
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ bgcolor: alpha("#f57c00", 0.1) }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography
                    variant="h4"
                    sx={{ color: "#f57c00", fontWeight: "bold" }}
                  >
                    {summaryStats.low}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Low Stock
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ bgcolor: alpha("#fbc02d", 0.1) }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography
                    variant="h4"
                    sx={{ color: "#fbc02d", fontWeight: "bold" }}
                  >
                    {summaryStats.moderate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Moderate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ bgcolor: alpha("#388e3c", 0.1) }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography
                    variant="h4"
                    sx={{ color: "#388e3c", fontWeight: "bold" }}
                  >
                    {summaryStats.healthy}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Healthy
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ bgcolor: alpha("#1976d2", 0.1) }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography
                    variant="h4"
                    sx={{ color: "#1976d2", fontWeight: "bold" }}
                  >
                    {summaryStats.overstocked}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overstocked
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {summaryStats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Items
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Financial Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography
                    variant="h5"
                    sx={{ color: theme.palette.info.main, fontWeight: "bold" }}
                  >
                    {formatCompactCurrency(summaryStats.totalAtaValue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Stock Value (ATA)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: theme.palette.warning.main,
                      fontWeight: "bold",
                    }}
                  >
                    {formatCompactCurrency(summaryStats.totalListValue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Stock Value (List Price)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: theme.palette.success.main,
                      fontWeight: "bold",
                    }}
                  >
                    {formatCompactCurrency(summaryStats.thirtyDayOpportunity)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    30-Day Opportunity (Low Stock)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Filters */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          select
          label="Volume Filter"
          value={volumeFilter}
          onChange={(e) => handleVolumeFilterChange(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {volumeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Status Filter"
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Search Items"
          variant="outlined"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search by item name..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 250,
            flexGrow: 1,
            maxWidth: 400,
          }}
        />
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 600,
          boxShadow: "none",
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderRadius: 2,
          overflow: "auto",
        }}
      >
        <Table stickyHeader aria-label="daily stock report table">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                "& th": {
                  fontWeight: "bold",
                },
              }}
            >
              <TableCell>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={createSortHandler("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ maxWidth: 150 }}>
                <TableSortLabel
                  active={orderBy === "item_type"}
                  direction={orderBy === "item_type" ? order : "asc"}
                  onClick={createSortHandler("item_type")}
                >
                  Item
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "stock_quantity"}
                  direction={orderBy === "stock_quantity" ? order : "asc"}
                  onClick={createSortHandler("stock_quantity")}
                >
                  Stock Qty
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "current_market_value"}
                  direction={orderBy === "current_market_value" ? order : "asc"}
                  onClick={createSortHandler("current_market_value")}
                >
                  Listed Value
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "estimated_value_all_time"}
                  direction={
                    orderBy === "estimated_value_all_time" ? order : "asc"
                  }
                  onClick={createSortHandler("estimated_value_all_time")}
                >
                  ATA Value
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "price_vs_avg"}
                  direction={orderBy === "price_vs_avg" ? order : "asc"}
                  onClick={createSortHandler("price_vs_avg")}
                >
                  Price vs Avg
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "months_supply_6mo"}
                  direction={orderBy === "months_supply_6mo" ? order : "asc"}
                  onClick={createSortHandler("months_supply_6mo")}
                >
                  Supply (mo)
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "rolling_6mo_avg_monthly_qty_sold"}
                  direction={
                    orderBy === "rolling_6mo_avg_monthly_qty_sold"
                      ? order
                      : "asc"
                  }
                  onClick={createSortHandler(
                    "rolling_6mo_avg_monthly_qty_sold",
                  )}
                >
                  Demand (qty/mo)
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "rolling_6mo_avg_monthly_volume"}
                  direction={
                    orderBy === "rolling_6mo_avg_monthly_volume" ? order : "asc"
                  }
                  onClick={createSortHandler("rolling_6mo_avg_monthly_volume")}
                >
                  Volume ($/mo)
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from(new Array(5)).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {Array.from(new Array(9)).map((_, cellIndex) => (
                      <TableCell key={`cell-${cellIndex}`}>
                        <Skeleton animation="wave" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : sortedData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.02,
                        ),
                      },
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.05,
                        ),
                      },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={row.status.label}
                        size="small"
                        sx={{
                          backgroundColor: alpha(row.status.color, 0.2),
                          color: row.status.color,
                          fontWeight: "bold",
                          fontSize: 11,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 150 }}>
                      <Tooltip title={row.item_type} arrow placement="top">
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "help",
                          }}
                        >
                          {row.item_type}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.stock_quantity)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCompactCurrency(row.current_market_value)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCompactCurrency(row.estimated_value_all_time)}
                    </TableCell>
                    <TableCell align="right">
                      {row.price_vs_avg !== null ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              row.price_vs_avg < 100 ? "#388e3c" : "#d32f2f",
                            fontWeight: "bold",
                          }}
                        >
                          {row.price_vs_avg >= 1000000 ? (
                            <span style={{ color: "#d32f2f" }}>∞</span>
                          ) : (
                            `${row.price_vs_avg.toFixed(0)}%`
                          )}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.months_supply_6mo, 1)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.rolling_6mo_avg_monthly_qty_sold, 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.rolling_6mo_avg_monthly_volume)}
                    </TableCell>
                  </TableRow>
                ))}
            {!loading && sortedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    No items found matching your filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {loading
            ? "Loading data..."
            : `Showing ${sortedData.length} of ${data.length} items`}
        </Typography>
        <Chip
          label={loading ? "..." : `${sortedData.length} items`}
          size="small"
          color="primary"
          variant="outlined"
        />
      </Box>
    </Box>
  );
};

export default DailyStockReport;
